import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  PlusIcon,
  VideoCameraIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { VideoCategory, VideoContent, VideoFilters, VideoMetadata } from '../types/video';
import { VideoForm } from '../components/forms/VideoForm';
import { VideoFormData } from '../types/video';
import { HeadphonesRecommendation } from '../types';
import { videoService } from '../services/videoService';
import { ProductType, ProductTypeLabels, videoProductTypes } from '../types/productType';
import { getErrorMessage } from '../utils/errors';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PageLayout } from '../components/ui/PageLayout';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Rueckfrage } from '../components/ui/Rueckfrage';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useRueckfrage } from '../hooks/useRueckfrage';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { produkttypAuswahl, seitenwechsel, statusAuswahl, zugriffAuswahl } from '../components/ui/filterZustand';

const mapVideoContentToFormData = (video: VideoContent): Partial<VideoFormData> => {
  // `videoFile` steht nach videoService.normalizeVideoContent() garantiert oben: die Methode
  // spiegelt contentData.videoFile dorthin, bevor irgendein Konsument die Zeile sieht. Der
  // frühere Fallback `contentData.videoFile` konnte deshalb nie greifen.
  const metadata: VideoMetadata = video.metadata || {};

  return {
    // Die API liefert `category`/`headphones` als lose Strings; die Formular-Typen sind enger.
    // Die Werte stammen aus derselben Taxonomie, deshalb hier eine gezielte Behauptung statt `any`.
    category: (video.category || metadata.category) as VideoCategory | undefined,
    title: video.title,
    description: video.description,
    shortDescription: video.shortDescription || metadata.shortDescription || '',
    duration: video.duration || metadata.duration || '',
    headphones: (video.headphones || metadata.headphones) as HeadphonesRecommendation | undefined,
    // Kein `keywords` mehr: das Formular führt das Feld nicht (VideoForm.buildFormData kennt es
    // nicht), die Zeile lud den Wert also in ein Feld, das es nicht gibt — und der Dienst schickte
    // dafür ein leerendes ''. Beides ist raus; die Stichworte bleiben am Datensatz stehen und
    // bleiben durchsuchbar.
    // Laufzeit-Slugs aus der Datenbank — kein Enum-Cast mehr (siehe PR #623), beide Seiten
    // sind `string[]`.
    topicCategories: video.topicCategories || metadata.topicCategories || [],
    videoUrl: video.videoFile?.url || '',
    thumbnailUrl: metadata.thumbnail || '',
    coverImageUrl: metadata.coverImage || '',
    // Affirmationen (string[]) → Textarea-String (eine pro Zeile) fürs Formular. Rückweg zu dem,
    // was videoService beim Speichern aufteilt — wortgleich zu AudioManagement.
    affirmations: Array.isArray(metadata.affirmations) ? metadata.affirmations.join('\n') : '',
    // Intro-Ende (Zahl) → Roh-String fürs Zahlenfeld. Ohne diese Zeile stünde das Feld beim
    // Bearbeiten leer, und ein Save schickte `null` — die gesetzte Marke wäre still gelöscht.
    // Ausdrücklich auf `number` geprüft: `String(null)` wäre „null" im Eingabefeld.
    introEndSeconds: typeof metadata.introEndSeconds === 'number' ? String(metadata.introEndSeconds) : '',
    artist: metadata.artist,
    // Kuratierter Interpret: FK-Spalte content.artistId (Fallback artistRef.id) → Dropdown pre-select.
    // Ohne diese Zeile stünde die Auswahlliste beim Bearbeiten immer auf „kein Interpret", und ein
    // Save löschte die bestehende Zuordnung.
    artistId: video.artistId ?? video.artistRef?.id ?? '',
    // Album = die kanonische content.album-Spalte; Fallback auf metadata.album für Alt-Rows.
    album: video.album ?? metadata.album,
    language: metadata.language,
    isPremium: video.isPremium,
    status: video.status,
    tags: video.tags || []
  };
};

const VideoManagement: React.FC = () => {
  const [videoContent, setVideoContent] = useState<VideoContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<VideoContent | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [filters, setFilters] = useState<VideoFilters>({
    page: 1,
    limit: 10,
    search: '',
    status: undefined,
    isPremium: undefined,
    category: undefined,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  // Zustand, Zeitmessung und Aufräumen des Balkens stecken im Haken. Der Fehler bleibt jetzt
  // stehen, statt nach drei Sekunden zu verschwinden — Begründung in `Benachrichtigung.tsx`.
  const { inhalt: benachrichtigung, melde, schließe } = useBenachrichtigung();
  // ⚠️ Das Feld heißt `rückfrage`, nicht `inhalt` — der Name daneben ist schon vergeben (hier sogar
  // zweimal umbenannt). Der Haken ist genau dafür so benannt, siehe `useRueckfrage.ts`.
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Memoisiert, damit der Effekt sie in seine Abhängigkeiten nehmen kann. Die Identität wechselt
  // genau dann, wenn sich `filters` ändert — `melde` ist über `useCallback` stabil.
  const fetchVideoContent = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await videoService.getVideoContent(filters);
      setVideoContent(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
    } catch (error) {
      console.error('Failed to fetch video content:', error);
      // Liste bei Fehler nicht leeren – sonst wirkt es, als wären Einträge „verschwunden“
      melde('error', 'Fehler beim Laden der Video-Inhalte');
    } finally {
      setIsLoading(false);
    }
  }, [filters, melde]);

  useEffect(() => {
    fetchVideoContent();
  }, [fetchVideoContent]);

  const handleFilterChange = <K extends keyof VideoFilters>(key: K, value: VideoFilters[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleCreateVideo = () => {
    setEditingVideo(null);
    setIsFormLoading(false);
    setShowForm(true);
  };

  const handleEditVideo = async (video: VideoContent) => {
    setShowForm(true);
    setIsFormLoading(true);
    setEditingVideo(null);
    try {
      const fullVideo = await videoService.getVideoContentById(video.id);
      setEditingVideo(fullVideo);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, 'Unbekannter Fehler beim Laden');
      melde('error', `Fehler beim Laden des Video-Inhalts: ${errorMessage}`);
      setShowForm(false);
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleDeleteVideo = async (id: string) => {
    // Der Titel wird VOR der Frage gelesen und danach nicht mehr angefasst: gelöscht wird über die
    // übergebene `id`, nicht über den Zustand. Damit kann zwischen Frage und Ausführung nichts
    // veralten — `editingVideo` darf sich ändern, das Ziel steht seit dem Klick fest.
    const titel = (editingVideo?.id === id ? editingVideo : videoContent.find((v) => v.id === id))?.title;
    // `gefahr`, und zwar unstrittig: das Backend fährt `tx.content.delete()` — ein echtes DELETE,
    // kein Statuswechsel (`VideoService.deleteVideoContent`). Was per Fremdschlüssel daran hängt,
    // räumt Postgres mit ab (`onDelete: Cascade` in `schema.prisma`).
    const bestätigt = await frage({
      titel: 'Video endgültig löschen?',
      text:
        `${titel ? `„${titel}"` : 'Dieser Video-Inhalt'} wird aus der Datenbank entfernt — nicht ` +
        'bloß auf Entwurf gesetzt und nicht archiviert.\n\n' +
        'Mit dem Eintrag verschwinden alle Verweise darauf: Favoriten, Bewertungen und ' +
        'Abruf-Zahlen der Nutzerinnen und Nutzer. Über diese Oberfläche ist das nicht rückgängig ' +
        'zu machen; nur die hochgeladenen Dateien bleiben im Medienspeicher liegen.',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      await videoService.deleteVideoContent(id);
      melde('success', 'Video-Inhalt erfolgreich gelöscht');
      // Form schließen, falls der Löschen-Button aus der Detail-Ansicht kam
      // (no-op, wenn ohnehin die Liste sichtbar war).
      setShowForm(false);
      setEditingVideo(null);
      fetchVideoContent();
    } catch (error) {
      console.error('Failed to delete video content:', error);
      melde('error', 'Fehler beim Löschen des Video-Inhalts');
    }
  };

  const handleFormSubmit = async (formData: VideoFormData) => {
    try {
      if (editingVideo) {
        const updated = await videoService.updateVideoContent(editingVideo.id, formData);
        melde('success', 'Video-Inhalt erfolgreich aktualisiert');
        setShowForm(false);
        setEditingVideo(null);
        // Aktualisierten Eintrag in der Liste ersetzen
        if (updated?.id) {
          setVideoContent(prev => prev.map(v => (v.id === updated.id ? updated : v)));
        }
      } else {
        const created = await videoService.createVideoContent(formData);
        melde('success', 'Video-Inhalt erfolgreich erstellt');
        setShowForm(false);
        setEditingVideo(null);
        // Neuen Eintrag sofort in die Liste einfügen (optimistisch), damit er nicht „verschwindet“
        if (created?.id) {
          setVideoContent(prev => [created, ...prev]);
          setPagination(prev => ({ ...prev, total: Math.max(0, prev.total) + 1 }));
        }
      }
      // Liste vom Server nachladen (falls optimistische Daten unvollständig sind)
      await new Promise(resolve => setTimeout(resolve, 150));
      await fetchVideoContent();
    } catch (error: unknown) {
      console.error('Failed to save video content:', error);
      melde('error', `Fehler beim ${editingVideo ? 'Aktualisieren' : 'Erstellen'} des Video-Inhalts: ${getErrorMessage(error, 'Unbekannter Fehler')}`);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingVideo(null);
  };


  const getCoverUrl = (video: VideoContent) => {
    return (
      video.metadata?.coverImage ||
      video.metadata?.thumbnail ||
      ''
    );
  };

  // initialData memoisiert, damit VideoForm nicht bei jedem Re-Render zurückgesetzt wird
  const initialData: Partial<VideoFormData> = useMemo(
    () => (editingVideo ? mapVideoContentToFormData(editingVideo) : {}),
    [editingVideo]
  );

  // Überschrift der Seitenvorlage. Hing vorher an drei getrennten `return`-Anweisungen; jetzt an
  // einer Stelle, damit es unter dem Balken nur noch EINE Weiche gibt.
  const seitenTitel = () => {
    if (!showForm) return 'Video-Verwaltung';
    if (isFormLoading) return 'Video laden...';
    return editingVideo ? 'Video bearbeiten' : 'Neues Video erstellen';
  };

  const formularZweig = () => {
    if (isFormLoading) {
      return (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Video-Inhalt...</p>
        </div>
      );
    }

    return (
      <VideoForm
        key={editingVideo?.id || 'new-video'}
        initialData={initialData}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        isLoading={isFormLoading}
        videoId={editingVideo?.id}
        onDelete={handleDeleteVideo}
      />
    );
  };

  const listenZweig = () => (
    <>
      {/* Feld-Definitionen aus `filterFelder.ts` — dieselben drei wie in der Audio-Verwaltung.
          Der einzige gemessene Unterschied war die Produkttyp-LISTE; sie ist der Parameter. */}
      <Filterleiste
        suche={filters.search || ''}
        onSuche={(wert) => handleFilterChange('search', wert)}
        platzhalter="Titel, Regisseur, Genre..."
        auswahlen={[
          produkttypAuswahl(videoProductTypes, filters.category, (wert) => handleFilterChange('category', wert)),
          statusAuswahl(filters.status, (wert) => handleFilterChange('status', wert)),
          zugriffAuswahl(filters.isPremium, (wert) => handleFilterChange('isPremium', wert)),
        ]}
      />

      {/* Content List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Video-Inhalte...</p>
        </div>
      ) : videoContent.length === 0 ? (
        <div className="text-center py-12 card">
          <VideoCameraIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine Video-Inhalte gefunden</p>
          <button
            onClick={handleCreateVideo}
            className="mt-4 btn btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Erstes Video erstellen
          </button>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css) statt aus handkopierten
              Klassenketten. Was dabei bewusst aufgegeben wurde, steht dort am Regelblock. */}
          <table className="table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Titel</th>
                <th>Künstler</th>
                <th>Produkttyp</th>
                <th>Status</th>
                <th>Zugriff</th>
              </tr>
            </thead>
            <tbody>
              {videoContent.map((video) => {
                const productCategory = video.productCategory
                  || video.metadata?.productCategory
                  || video.category
                  || video.metadata?.category
                  || video.contentData?.productCategory;
                const productTypeLabel = productCategory
                  ? (ProductTypeLabels[productCategory as ProductType] || productCategory)
                  : '–';
                const artist = video.metadata?.artist
                  || video.metadata?.director
                  || '-';
                return (
                <tr key={video.id}>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleEditVideo(video)}
                      className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                      title="Bearbeiten"
                    >
                      {getCoverUrl(video) ? (
                        <img
                          src={getCoverUrl(video)}
                          alt={video.title}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center">
                          <PhotoIcon className="w-5 h-5 text-secondary-400" />
                        </div>
                      )}
                    </button>
                  </td>
                  <td className="max-w-xs">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => handleEditVideo(video)}
                        className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer text-left truncate max-w-[70ch] focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                        title={video.title}
                      >
                        {video.title.length > 70 ? video.title.substring(0, 70) + '...' : video.title}
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm text-secondary-900">{artist}</div>
                  </td>
                  <td>
                    <div className="text-sm text-secondary-900">{productTypeLabel}</div>
                  </td>
                  <td>
                    <StatusBadge status={video.status} />
                  </td>
                  <td>
                    <div className="flex space-x-2">
                      {video.isPremium
                        ? <span className="badge badge-primary">Premium</span>
                        : <span className="badge badge-secondary">Free</span>}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <SeitenblaetterFuss
            seite={pagination.page}
            seitenAnzahl={pagination.totalPages}
            gesamt={pagination.total}
            onSeitenwechsel={seitenwechsel(setFilters)}
          />
        </div>
      )}
    </>
  );

  return (
    <PageLayout
      title={seitenTitel()}
      actions={
        showForm ? undefined : (
          <button
            onClick={handleCreateVideo}
            className="btn btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Neues Video
          </button>
        )
      }
    >
      {/* ⚠️ EIN Balken, und zwar VOR der Weiche — das ist der Fix, nicht die Kosmetik daran.
          Vorher endete `if (showForm)` in einem eigenen `return`, und der Balken stand erst
          dahinter, also nur im Listen-Zweig. `handleFormSubmit` meldet aber im `catch` und lässt
          das Formular bewusst OFFEN, damit die Eingaben nicht verloren gehen: die Fehlermeldung
          ging damit an einen Balken, der in genau diesem Zustand gar nicht im Baum stand. Die
          Nutzerin klickte „Video speichern", der Server lehnte ab, und sichtbar passierte nichts.
          Mit der Weiche UNTER dem Balken ist dieser Zustand nicht mehr baubar — derselbe Umbau
          steht in `AudioManagement.tsx`, wo die volle Herleitung nachzulesen ist.

          ⚠️ Unterschied zur Audio-Verwaltung, nachgemessen statt übernommen: `VideoForm` hält
          KEINE zweite Meldungs-Quelle. Es hat weder `useBenachrichtigung` noch einen eigenen
          `<Benachrichtigung>`; sein `uploadError` ist ein Feld-Hinweis
          (`<p className="mt-2 text-sm text-error-600">`, drei Stellen) direkt am jeweiligen
          Upload-Feld, kein Balken. Deshalb bekommt es hier auch keine `notify`-Eigenschaft
          gereicht — es gäbe nichts zusammenzulegen. Ebenso geprüft: `schließe` wird auf dieser
          Seite an genau einer Stelle benutzt, nämlich am Schließen-Knopf des Balkens unten. Es
          gibt also keinen Aufruf, der eine stehende Speicher-Fehlermeldung wegräumen könnte.

          `schwebend` nur im Formular-Zweig — aus den DEKLARIERTEN Werten gerechnet, wie bei Audio:
          • `VideoForm` hat eine eigene festgestellte Knopfleiste (`fixed bottom-0 right-0 left-64
            z-30`) und öffnet den Datei-Wähler (`.bunny-file-picker-overlay`: `fixed inset-0 z-50`
            in `index.css`). Im Textfluss stünde der Balken hinter dem Wähler-Schleier und
            außerhalb des Sichtfelds.
          • `schwebend` bringt `fixed top-4 right-4 z-[60]` mit: 60 schlägt Wähler (50) und
            Knopfleiste (30) erklärt statt über die Dokumentreihenfolge. Der Balken steht VOR
            beiden im Baum, bei Gleichstand verlöre er also.
          • OBEN rechts, nicht unten: unten rechts klebt die Knopfleiste des Formulars an derselben
            Kante und trägt dort „Video speichern".
          • Der Listen-Zweig hat weder Overlay noch festgestellte Leiste und bleibt deshalb bei der
            Mehrheitsfassung im Textfluss.

          ⚠️ Was diese Rechnung NICHT ist: eine Trefferprobe im laufenden Browser. Unter Vitest ist
          sie gar nicht messbar — jsdom hat kein Layout, `getBoundingClientRect` meldet Nullen. Die
          Tests nageln deshalb die deklarierten Stapel- und Positionswerte fest, nicht Pixel. */}
      <Benachrichtigung inhalt={benachrichtigung} onSchließen={schließe} schwebend={showForm} />

      {/* Die Rückfrage steht ÜBER der Weiche, genau wie der Balken darüber — und der Grund ist
          derselbe.

          ⚠️ Gemessen, nicht von der Nachbarseite übernommen: gefragt wird hier heute
          ausschließlich aus dem FORMULAR-Zweig. `handleDeleteVideo` hat genau einen Aufrufer,
          nämlich die `onDelete`-Eigenschaft von `VideoForm` (deren Knopf „Video löschen" in der
          festgestellten Leiste); die Liste hat keine Löschen-Spalte. Trotzdem gehört der Dialog
          NICHT in `formularZweig()`, und das ist keine Vorsicht auf Verdacht:
          • `formularZweig()` hat selbst ZWEI Rückgaben — im Ladezustand kommt der Spinner zurück
            und `VideoForm` gar nicht. Ein Dialog neben dem Formular stünde in genau einer davon.
          • Der Erfolgspfad ruft `setShowForm(false)`: der Zweig, in dem gefragt wurde, verschwindet
            als Folge der Antwort. Hier oben ist das folgenlos, drinnen wäre es eine Abhängigkeit
            von der Reihenfolge zweier Zustandsänderungen.
          • Die Weiche selbst ist keine Zusage: bekäme die Liste morgen einen Löschen-Knopf, wäre
            die Frage aus dem Listen-Zweig unsichtbar — dieselbe Fehlerklasse, die den Balken
            hierher gebracht hat.

          Die Stapelordnung braucht auf DIESER Seite keine eigene Rechnung: der Dialog-Schleier
          liegt mit `z-[55]` über dem Datei-Wähler des Formulars (`z-50`) und unter dem schwebenden
          Balken (`z-[60]`). Hergeleitet und im Browser nachgemessen ist das an der Themen-Seite,
          die als einzige alle drei Schichten gleichzeitig zeigen kann. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {showForm ? formularZweig() : listenZweig()}
    </PageLayout>
  );
};

export default VideoManagement;
